import { ComponentType, FC } from 'react';
import classNames from 'classnames';
import Avatar, { AvatarSize, AvatarProps } from './Avatar';

export interface AvatarGroupProps<P extends AvatarProps = AvatarProps> {
  /** Array of avatar props to display */
  avatars: Omit<P, 'size' | 'variant'>[];

  /**
   * What to render each entry with. Defaults to Avatar; pass MediaAvatar
   * when the entries carry a `mediaId`.
   */
  component?: ComponentType<P>;

  /** Maximum number of avatars to display before showing "+N" */
  max?: number;

  /** Size of all avatars in the group */
  size?: AvatarSize;

  /** Show an "Add" button at the end */
  showAddButton?: boolean;

  /** Callback when the "Add" button is clicked */
  onAddClick?: () => void;

  /** What the "Add" button does, for screen readers (e.g. "Add member"). */
  addLabel?: string;

  /**
   * Ring colour separating overlapping avatars: the colour of whatever the
   * group sits on, e.g. `ring-surface-panel`.
   */
  ringClassName?: string;

  /** Additional CSS classes */
  className?: string;
}

// Size-specific configurations for the group
const groupSizeConfig: Record<
  AvatarSize,
  {
    overlap: string;
    border: string;
    countText: string;
    addButtonSize: string;
    addIconSize: string;
  }
> = {
  '3xs': {
    overlap: '-ml-1',
    border: 'ring-[1.5px]',
    countText: 'text-[7px]',
    addButtonSize: 'w-4 h-4',
    addIconSize: 'w-2 h-2',
  },
  xxs: {
    overlap: '-ml-1.5',
    border: 'ring-2',
    countText: 'text-[10px]',
    addButtonSize: 'w-5 h-5',
    addIconSize: 'w-2.5 h-2.5',
  },
  xs: {
    overlap: '-ml-2',
    border: 'ring-2',
    countText: 'text-xs',
    addButtonSize: 'w-8 h-8',
    addIconSize: 'w-3 h-3',
  },
  sm: {
    overlap: '-ml-2.5',
    border: 'ring-2',
    countText: 'text-sm',
    addButtonSize: 'w-10 h-10',
    addIconSize: 'w-4 h-4',
  },
  md: {
    overlap: '-ml-3',
    border: 'ring-2',
    countText: 'text-base',
    addButtonSize: 'w-12 h-12',
    addIconSize: 'w-5 h-5',
  },
  lg: {
    overlap: '-ml-3.5',
    border: 'ring-[3px]',
    countText: 'text-lg',
    addButtonSize: 'w-14 h-14',
    addIconSize: 'w-5 h-5',
  },
  xl: {
    overlap: '-ml-4',
    border: 'ring-[3px]',
    countText: 'text-xl',
    addButtonSize: 'w-16 h-16',
    addIconSize: 'w-6 h-6',
  },
  '2xl': {
    overlap: '-ml-5',
    border: 'ring-4',
    countText: 'text-2xl',
    addButtonSize: 'w-20 h-20',
    addIconSize: 'w-7 h-7',
  },
};

// Plus icon for the add button
const PlusIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const AvatarGroup = <P extends AvatarProps = AvatarProps>({
  avatars,
  component,
  max = 5,
  size = 'md',
  showAddButton = false,
  onAddClick,
  addLabel,
  ringClassName = 'ring-white',
  className,
}: AvatarGroupProps<P>) => {
  const Item = (component ?? Avatar) as ComponentType<P>;
  const config = groupSizeConfig[size];
  const ring = classNames(config.border, ringClassName);

  // Calculate visible avatars and overflow count
  const visibleAvatars = avatars.slice(0, max);
  const overflowCount = Math.max(0, avatars.length - max);

  // Render the overflow count badge (+N)
  const renderOverflowBadge = () => {
    if (overflowCount === 0) return null;

    return (
      <div
        className={classNames(
          'relative inline-flex items-center justify-center',
          'rounded-full bg-ink-secondary/15',
          ring,
          config.overlap,
          config.addButtonSize,
        )}
      >
        <span
          className={classNames(
            'font-medium text-ink-secondary',
            config.countText,
          )}
        >
          +{overflowCount}
        </span>
      </div>
    );
  };

  // Render the add button
  const renderAddButton = () => {
    if (!showAddButton) return null;

    return (
      <button
        type="button"
        onClick={onAddClick}
        className={classNames(
          'relative inline-flex items-center justify-center',
          'rounded-full border-2 border-dashed border-mf-gray-300',
          'hover:border-mf-gray-400 hover:bg-mf-gray-50 transition-colors',
          'ml-2',
          config.addButtonSize,
        )}
        aria-label={addLabel}
      >
        <PlusIcon
          className={classNames('text-mf-gray-400', config.addIconSize)}
        />
      </button>
    );
  };

  return (
    <div className={classNames('inline-flex items-center', className)}>
      {visibleAvatars.map((avatarProps, index) => (
        <div
          key={index}
          // inline-flex, not block: a block wrapper adds line-height space
          // under the avatar and the ring stretches into an oval.
          className={classNames(
            'relative inline-flex rounded-full',
            ring,
            index > 0 && config.overlap,
          )}
          style={{ zIndex: visibleAvatars.length - index }}
        >
          <Item {...(avatarProps as P)} size={size} variant="default" />
        </div>
      ))}
      {renderOverflowBadge()}
      {renderAddButton()}
    </div>
  );
};

export default AvatarGroup;
